import type { components } from '@front/generated/apiClient';
import { apiConverter } from '@front/repository/Api/converter';
import { getApiClient } from '@front/repository/common/apiClient';
import type { IImportTableDefinitionRepository as I } from './interface';

type CreateBody = components['schemas']['ImportTableDefinition.CreateBody'];
type UpdateBody = components['schemas']['ImportTableDefinition.UpdateBody'];
type CreateRevisionBody = components['schemas']['ImportTableDefinition.CreateRevisionBody'];
type CheckRevisionCompatibilityBody = components['schemas']['ImportTableDefinition.CheckRevisionCompatibilityBody'];

export class ImportTableDefinitionRepository implements I {
  list: I.List = async (params, options) => {
    const res = await getApiClient().POST('/import-table-definition/list', {
      body: {
        projectId: params.projectId,
        sourceSystemId: params.sourceSystemId,
      },
      signal: options?.signal,
    });
    if (!res.data) throw new Error('Failed to list import table definitions');
    return res.data.map(apiConverter.ImportTableDefinition.detailFromApi);
  };

  get: I.Get = async (params, options) => {
    const res = await getApiClient().POST('/import-table-definition/get', {
      body: { projectId: params.projectId, id: params.id },
      signal: options?.signal,
    });
    if (!res.response.ok) throw new Error('Failed to get import table definition');
    return res.data ? apiConverter.ImportTableDefinition.detailFromApi(res.data) : undefined;
  };

  create: I.Create = async ({ projectId, createProps, fileId }, options) => {
    const body: CreateBody = {
      projectId,
      sourceSystemId: createProps.sourceSystemId,
      displayName: createProps.displayName,
      description: createProps.description,
      schemaJson: createProps.schemaJson as CreateBody['schemaJson'],
      uniqueKeys: createProps.uniqueKeys,
      loadMode: createProps.loadMode as CreateBody['loadMode'],
      importMethod: createProps.importMethod as CreateBody['importMethod'],
      isActive: createProps.isActive,
      fileId,
    };
    const res = await getApiClient().POST('/import-table-definition/create', {
      body,
      signal: options?.signal,
    });
    if (!res.data) throw new Error('Failed to create import table definition');
    return {
      detail: apiConverter.ImportTableDefinition.detailFromApi({
        definition: res.data.definition,
        activeRevision: undefined,
        lastRevision: res.data.lastRevision,
      }),
      task: res.data.task ? apiConverter.ImportTask.fromApi(res.data.task) : undefined,
    };
  };

  update: I.Update = async ({ projectId, id, updateProps }, options) => {
    const body = {
      projectId,
      id,
      ...updateProps,
    } as UpdateBody;
    const res = await getApiClient().POST('/import-table-definition/update', {
      body,
      signal: options?.signal,
    });
    if (!res.data) throw new Error('Failed to update import table definition');
    return apiConverter.ImportTableDefinition.detailFromApi(res.data);
  };

  remove: I.Remove = async ({ projectId, id }, options) => {
    const res = await getApiClient().POST('/import-table-definition/delete', {
      body: { projectId, id },
      signal: options?.signal,
    });
    if (!res.response.ok) throw new Error('Failed to delete import table definition');
  };

  listRevisions: I.ListRevisions = async ({ projectId, id }, options) => {
    const res = await getApiClient().POST('/import-table-definition/revisions/list', {
      body: { projectId, id },
      signal: options?.signal,
    });
    if (!res.data) throw new Error('Failed to list import table definition revisions');
    return res.data.map(apiConverter.ImportTableDefinition.revisionFromApi);
  };

  createRevision: I.CreateRevision = async ({ projectId, id, props }, options) => {
    const body: CreateRevisionBody = {
      projectId,
      id,
      schemaJson: props.schemaJson as CreateRevisionBody['schemaJson'],
      uniqueKeys: props.uniqueKeys,
      loadMode: props.loadMode as CreateRevisionBody['loadMode'],
      importMethod: props.importMethod as CreateRevisionBody['importMethod'],
    };
    const res = await getApiClient().POST('/import-table-definition/revisions/create', {
      body,
      signal: options?.signal,
    });
    if (!res.data) throw new Error('Failed to create import table definition revision');
    return {
      definition: apiConverter.ImportTableDefinition.definitionFromApi(res.data.definition),
      revision: apiConverter.ImportTableDefinition.revisionFromApi(res.data.revision),
    };
  };

  checkRevisionCompatibility: I.CheckRevisionCompatibility = async ({ projectId, id, props }, options) => {
    const body: CheckRevisionCompatibilityBody = {
      projectId,
      id,
      schemaJson: props.schemaJson as CheckRevisionCompatibilityBody['schemaJson'],
      uniqueKeys: props.uniqueKeys,
      loadMode: props.loadMode as CheckRevisionCompatibilityBody['loadMode'],
      importMethod: props.importMethod as CheckRevisionCompatibilityBody['importMethod'],
    };
    const res = await getApiClient().POST('/import-table-definition/revisions/check-compatibility', {
      body,
      signal: options?.signal,
    });
    if (!res.data) throw new Error('Failed to check import table definition revision compatibility');
    return {
      compatibilityType: res.data.compatibilityType,
      hasDiff: res.data.hasDiff,
      changeSummary: res.data.changeSummary ?? {},
      breakingReasons: res.data.breakingReasons,
    };
  };

  activateRevision: I.ActivateRevision = async ({ projectId, id, revisionId }, options) => {
    const res = await getApiClient().POST('/import-table-definition/revisions/activate', {
      body: { projectId, id, revisionId },
      signal: options?.signal,
    });
    if (!res.data) throw new Error('Failed to activate import table definition revision');
    return apiConverter.ImportTableDefinition.detailFromApi(res.data);
  };
}
